import React, { useState, useEffect } from 'react';
import {
    Briefcase,
    Plus,
    Search,
    Edit,
    Trash2,
    RefreshCw,
    ExternalLink,
    Image as ImageIcon
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useModal } from '../ui/ModalProvider';

const ProjectManagement = () => {
    const { showConfirm } = useModal();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const { toast } = useToast();

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image: '',
        tags: '',
        link: ''
    });

    useEffect(() => {
        loadProjects();
    }, []);

    const loadProjects = async () => {
        try {
            setLoading(true);
            // We need to implement getProjects in adminApi or contentApi
            const response = await adminApi.getProjects();
            setProjects(response.data);
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to load projects",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (project) => {
        setEditingProject(project);
        setFormData({
            title: project.title,
            description: project.description,
            image: project.image || '',
            tags: project.tags ? project.tags.join(', ') : '',
            link: project.link || ''
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        showConfirm('Are you sure you want to delete this project?', async () => {
            try {
                await adminApi.deleteProject(id);
                setProjects(prev => prev.filter(p => p.id !== id));
                toast({ title: "Deleted", description: "Project removed successfully" });
            } catch (error) {
                toast({ title: "Error", description: "Failed to delete project", variant: "destructive" });
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = {
            ...formData,
            tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== '')
        };

        try {
            if (editingProject) {
                const response = await adminApi.updateProject(editingProject.id, data);
                setProjects(prev => prev.map(p => p.id === editingProject.id ? response.data : p));
                toast({ title: "Updated", description: "Project updated successfully" });
            } else {
                const response = await adminApi.createProject(data);
                setProjects(prev => [response.data, ...prev]);
                toast({ title: "Created", description: "Project created successfully" });
            }
            setIsModalOpen(false);
            setEditingProject(null);
            setFormData({ title: '', description: '', image: '', tags: '', link: '' });
        } catch (error) {
            toast({ title: "Error", description: "Failed to save project", variant: "destructive" });
        }
    };

    const filteredProjects = projects.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-gray-600 border-t-silver rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-silver mb-2">Projects</h1>
                    <p className="text-gray-400">Manage your portfolio and case studies</p>
                </div>
                <button
                    onClick={() => {
                        setEditingProject(null);
                        setFormData({ title: '', description: '', image: '', tags: '', link: '' });
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-silver text-black rounded-lg hover:bg-white transition-colors font-medium"
                >
                    <Plus size={20} />
                    Add Project
                </button>
            </div>

            <div className="glass-morphism rounded-xl p-6">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search projects..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-silver focus:border-silver outline-none"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project) => (
                    <div key={project.id} className="glass-morphism rounded-xl overflow-hidden flex flex-col group">
                        <div className="aspect-video relative bg-gray-800 overflow-hidden">
                            {project.image ? (
                                <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-600">
                                    <ImageIcon size={48} />
                                </div>
                            )}
                        </div>
                        <div className="p-6 flex-1 flex flex-col space-y-4">
                            <div>
                                <h3 className="text-xl font-bold text-silver mb-2">{project.title}</h3>
                                <p className="text-gray-400 text-sm line-clamp-2">{project.description}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {project.tags?.map((tag, i) => (
                                    <span key={i} className="px-2 py-1 bg-white/5 text-gray-400 text-xs rounded">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                            <div className="flex items-center justify-between pt-4 mt-auto border-t border-white/5">
                                <div className="flex gap-2">
                                    <button onClick={() => handleEdit(project)} className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors">
                                        <Edit size={18} />
                                    </button>
                                    <button onClick={() => handleDelete(project.id)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors">
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                                {project.link && (
                                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-silver transition-colors">
                                        <ExternalLink size={18} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="glass-morphism w-full max-w-xl rounded-2xl border border-gray-700 overflow-hidden">
                        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-silver">
                                {editingProject ? 'Edit Project' : 'Add New Project'}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                                <Plus size={24} className="rotate-45" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-400">Title</label>
                                <input
                                    required
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none"
                                    placeholder="Project name"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-400">Description</label>
                                <textarea
                                    required
                                    rows={3}
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none resize-none"
                                    placeholder="Brief overview"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-400">Image URL</label>
                                <input
                                    type="text"
                                    value={formData.image}
                                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                                    className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none"
                                    placeholder="https://example.com/image.jpg"
                                />
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-400">Tags (comma separated)</label>
                                    <input
                                        type="text"
                                        value={formData.tags}
                                        onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                                        className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none"
                                        placeholder="React, AWS, Node.js"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-400">Project Link</label>
                                    <input
                                        type="text"
                                        value={formData.link}
                                        onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                                        className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-2 text-silver focus:border-silver outline-none"
                                        placeholder="https://project-url.com"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-3 bg-gray-800 text-silver rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-3 bg-silver text-black font-bold rounded-lg hover:bg-white transition-colors"
                                >
                                    {editingProject ? 'Save Changes' : 'Create Project'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectManagement;
